/**
 * PersonaBadge - Shows which AI persona is currently working on a stage
 * Displays persona name, model, and current action
 */

import React from 'react';

import { cn } from '@/utils/cn';

import './pipeline-animations.css';

export interface PersonaDetails {
  name: string;  // "The Architect", "The Captain", "The Operator", etc.
  model: string; // "Claude Opus 4.1", "GPT-5", etc.
  action: string; // "Creating vision", "Evaluating quality", etc.
  role: 'generator' | 'evaluator';
}

interface PersonaBadgeProps {
  persona: PersonaDetails;
  isActive?: boolean;
  isSwitching?: boolean;
  compact?: boolean;
  className?: string;
}

const PersonaBadge: React.FC<PersonaBadgeProps> = ({
  persona,
  isActive = false,
  isSwitching = false,
  compact = false,
  className
}) => {
  const getPersonaStyles = (role: 'generator' | 'evaluator'): React.CSSProperties => {
    return role === 'generator' 
      ? {
          backgroundColor: 'var(--info-bg-subtle)',
          borderColor: 'var(--info-border-subtle)',
          color: 'var(--info)'
        }
      : {
          backgroundColor: 'var(--accent-bg-subtle)',
          borderColor: 'var(--accent-border-subtle)', 
          color: 'var(--accent)'
        };
  };

  const getModelBadgeStyles = (model: string): React.CSSProperties => {
    if (model.includes('Claude') || model.includes('Opus')) {
      return {
        backgroundColor: 'var(--warning-bg-subtle)',
        color: 'var(--warning)'
      };
    }
    if (model.includes('GPT')) {
      return {
        backgroundColor: 'var(--success-bg-subtle)',
        color: 'var(--success)'
      };
    }
    if (model.includes('Gemini')) {
      return {
        backgroundColor: 'var(--info-bg-subtle)',
        color: 'var(--info)'
      };
    }
    return {
      backgroundColor: 'var(--bg-subtle)',
      color: 'var(--fg-muted)'
    };
  };

  const personaStyles = getPersonaStyles(persona.role);
  const modelStyles = getModelBadgeStyles(persona.model);

  if (compact) {
    // Compact view - just icon and name
    return (
      <div
        className={cn(
          'inline-flex items-center border transition-all',
          isActive && 'ring-2 ring-offset-1',
          isSwitching && 'persona-badge switching',
          className
        )}
        style={{
          ...personaStyles,
          gap: 'var(--space-1)',
          padding: 'var(--space-2)',
          borderRadius: 'var(--radius-full)',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-medium)',
          transitionDuration: 'var(--motion-duration-fast)'
        }}
        title={`${persona.name} (${persona.model}): ${persona.action}`}
      >
        <span>{persona.name}</span>
      </div>
    );
  }

  // Full view with all details
  return (
    <div
      className={cn(
        'persona-badge inline-flex items-center transition-all',
        isActive && 'ring-2 ring-offset-2 shadow-lg scale-105',
        isSwitching && 'switching',
        className
      )}
      style={{
        ...personaStyles,
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        borderWidth: '2px',
        transitionDuration: 'var(--motion-duration-fast)'
      }}
    >
      {/* Persona Details */}
      <div className="flex-1 space-y-1">
        <div className="flex items-center space-x-2">
          <span className="font-semibold text-sm persona-name">{persona.name}</span>
          <span 
            className="rounded font-medium"
            style={{
              ...modelStyles,
              padding: 'var(--space-1) var(--space-2)',
              fontSize: 'var(--font-size-xs)'
            }}
          >
            {persona.model}
          </span>
        </div>
        <div className="text-xs opacity-90 persona-action">
          {persona.action}
        </div>
      </div>

      {/* Active Indicator */}
      {isActive && (
        <div className="flex-shrink-0">
          <div className="w-2 h-2 bg-current rounded-full animate-pulse" />
        </div>
      )}
    </div>
  );
};

// Persona Gallery Component for showing multiple personas
export const PersonaGallery: React.FC<{
  generator?: PersonaDetails;
  evaluator?: PersonaDetails;
  activeRole: 'generator' | 'evaluator';
  className?: string;
}> = ({ generator, evaluator, activeRole, className }) => {
  return (
    <div className={cn('flex items-center justify-center space-x-4', className)}>
      {generator && (
        <div className="flex flex-col items-center space-y-2">
          <span 
            className="font-medium"
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--fg-muted)'
            }}
          >
            Generator
          </span>
          <PersonaBadge
            persona={generator}
            isActive={activeRole === 'generator'}
            compact
          />
        </div>
      )}

      {generator && evaluator && (
        <div className="flex items-center">
          <div style={{ color: 'var(--fg-muted)' }}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          </div>
        </div>
      )}

      {evaluator && (
        <div className="flex flex-col items-center space-y-2">
          <span 
            className="font-medium"
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--fg-muted)'
            }}
          >
            Evaluator
          </span>
          <PersonaBadge
            persona={evaluator}
            isActive={activeRole === 'evaluator'}
            compact
          />
        </div>
      )}
    </div>
  );
};

// Preset personas for HI-SI stages
export const HISI_PERSONAS = {
  script: {
    generator: {
      name: 'The Writer',
      model: 'Claude Opus 4.1',
      action: 'Crafting compelling narrative',
      role: 'generator' as const
    },
    evaluator: {
      name: 'The Editor',
      model: 'GPT-5',
      action: 'Evaluating script quality',
      role: 'evaluator' as const
    }
  },
  vision: {
    generator: {
      name: 'The Architect',
      model: 'Claude Opus 4.1',
      action: 'Designing visual language',
      role: 'generator' as const
    },
    evaluator: {
      name: 'The Captain',
      model: 'GPT-5',
      action: 'Preventing visual clichés',
      role: 'evaluator' as const
    }
  },
  shots: {
    generator: {
      name: 'The Operator',
      model: 'Claude Opus 4.1',
      action: 'Planning shot composition',
      role: 'generator' as const
    },
    evaluator: {
      name: 'The Inspector',
      model: 'GPT-5',
      action: 'Ensuring operational precision',
      role: 'evaluator' as const
    }
  },
  frames: {
    generator: {
      name: 'The Artist',
      model: 'DALL-E 3',
      action: 'Generating keyframes',
      role: 'generator' as const
    },
    evaluator: {
      name: 'The Critic',
      model: 'GPT-5 Vision',
      action: 'Assessing visual quality',
      role: 'evaluator' as const
    }
  },
  video: {
    generator: {
      name: 'The Director',
      model: 'Kling 1.6',
      action: 'Creating motion',
      role: 'generator' as const
    },
    evaluator: {
      name: 'The Producer',
      model: 'GPT-5',
      action: 'Final quality check',
      role: 'evaluator' as const
    }
  }
};

export default PersonaBadge;